<template>
  <UserPopover :is-active="hasPopover" :user="user" :position="popoverPosition">
    <img
      v-if="user?.picture"
      :src="user.picture"
      :alt="user.name"
      class="inline-block rounded-full"
      :class="sizeClasses"
    />
    <span
      v-else
      class="inline-flex items-center justify-center rounded-full"
      :class="[sizeClasses, user ? `bg-${backgroundColor}` : 'bg-gray-500']"
    >
      <span
        v-if="user"
        class="select-none font-medium leading-none"
        :class="textColorClass"
        :style="{ fontSize: fontSize }"
      >
        {{ letters }}
      </span>
      <UserIcon v-else class="size-4 text-white" />
    </span>
  </UserPopover>
</template>

<script lang="ts">
export type UserAvatarUser = {
  _id: string;
  name: string;
  picture?: string;
};
</script>

<script lang="ts" setup>
import { UserIcon } from '@heroicons/vue/20/solid';
import {
  getForeground,
  getBackgroundColorByIndex,
  getHexByColorBrightness,
} from '@/util/colors';
import type { ColorBrightness } from '@/util/colors';
import type { PopoverPosition } from '@/types';

const props = defineProps({
  user: {
    type: Object as PropType<UserAvatarUser>,
  },
  size: {
    type: String,
    default: '10',
  },
  hasPopover: {
    type: Boolean,
    default: true,
  },
  popoverPosition: {
    type: String as PropType<PopoverPosition>,
    default: 'left',
  },
});

const fontSize = computed(() => {
  const avatarSize = parseInt(props.size, 10);
  const fontSizeIncreaseFactor = props.user ? 1.4 : 1;
  return `${avatarSize * fontSizeIncreaseFactor}px`;
});

const sizeClasses = computed(() => {
  return `size-${props.size}`;
});

const letters = computed(() => {
  if (!props.user?.name) {
    return 'N/A';
  }
  const nameParts = props.user?.name.split(' ');
  const letters = nameParts
    .map((element) => element[0]?.toUpperCase())
    .slice(0, 2);
  return letters.join('');
});

const letterIndexInAlphabet = computed(() => {
  const letter = letters.value[0].toUpperCase();

  // Get the ASCII code of the letter
  const charCode = letter.charCodeAt(0);

  let secondCharCode = 0;
  if (letters.value.length > 1) {
    const secondLetter = letters.value[1].toUpperCase();
    secondCharCode = secondLetter.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
  }

  // Subtract the ASCII code of 'A' to get the index in the alphabet
  return charCode - 'A'.charCodeAt(0) + 1 + secondCharCode;
});

const backgroundColor = computed(() => {
  if (!props.user?.name) {
    return 'gray-500';
  }
  return getBackgroundColorByIndex(letterIndexInAlphabet.value);
});

const textColorClass = computed(() => {
  if (!backgroundColor?.value) {
    return;
  }
  if (!props.user) {
    return 'text-white';
  }

  const splittedColor = backgroundColor.value.split('-');
  const hexColor = getHexByColorBrightness(
    splittedColor[0],
    splittedColor[1] as ColorBrightness
  );
  const textColor = getForeground(hexColor);
  return textColor === 'black' ? 'text-black' : 'text-white';
});
</script>
